/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { GridOffset } from "@insite/mobius/GridContainer";
import Tag from "@insite/mobius/Icons/Tag";
import { StyledClickable } from "@insite/mobius/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import { wishListCardStyles } from "@monsma/Overrides/Components/WishListCard";
import { screen } from "@monsma/XDL/Helpers";
import SignOutAlt from "@monsma/XDL/Icons/SignOutAlt";
import { removeLinkStyle } from "@monsma/XDL/Utilities/sharedStyles";
import { css } from "styled-components";

wishListCardStyles.basicInfoGridContainer = {
    gap: 0,
    css: css`
        padding-right: 15px;

        &&& > ${GridOffset} {
            width: 100%;
        }

        ${StyledClickable} ${TypographyStyle} {
            letter-spacing: 0;
            font-size: 16px;
            line-height: 20px;
            font-weight: bold;

            ${screen("lte", "sm")} {
                margin-right: 40px;
            }
        }
    `,
};

wishListCardStyles.extendedInfoGridItem = {
    width: [12, 12, 7, 8, 8],
    align: "top",
};

wishListCardStyles.productImageLink = {
    css: css`
        width: 76px;
        height: 76px;
        border: 1px solid ${getColor("common.ui12")};
        border-radius: 4px;
        background-color: ${getColor("common.background")};
        padding: 5px;

        &:focus,
        &:hover {
            outline: none;
            border-color: ${getColor("text")};
        }
    `,
    typographyProps: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 100%;
        `,
    },
};

wishListCardStyles.productImagesGridItem = {
    width: [12, 12, 12, 8, 8],
    css: css`
        flex-wrap: wrap;

        ${screen("lte", "sm")} {
            &&& {
                justify-content: center;
                margin-top: 10px;
            }
        }
    `,
};

wishListCardStyles.sharingStatusGridItem = {
    width: [12, 12, 12, 4, 4],
    css: css`
        align-items: center;
        justify-content: left;
    `,
};

wishListCardStyles.actionGridItem = {
    css: css`
        ${screen("lte", "sm")} {
            flex: 1 1 100%;
            border-left-width: 0;
            justify-content: flex-start;
            padding: 0 20px 20px;
        }
    `,
};

wishListCardStyles.actionAddToCartButton = {
    sizeVariant: "small",
};

wishListCardStyles.actionDeleteButton = {
    variant: "secondary",
    sizeVariant: "small",
    css: css`
        && {
            width: 100%;
            margin-top: 10px;
        }
    `,
};

wishListCardStyles.actionLeaveButton = {
    variant: "secondary",
    sizeVariant: "small",
    css: css`
        && {
            width: 100%;
            margin-top: 10px;
        }
    `,
};

wishListCardStyles.descriptionText = {
    css: css`
        margin-top: 12px;
        font-size: 13px;
    `,
};

wishListCardStyles.lastUpdatedText = {
    css: css`
        margin-top: 7px;
        font-size: 13px;
        letter-spacing: 0;
        line-height: 22px;

        ${screen("lte", "sm")} {
            margin-right: 40px;
        }
    `,
};

wishListCardStyles.wishListInfoGridContainer = {
    gap: 0,
    css: css`
        &&& > ${GridOffset} {
            width: 100%;
        }
    `,
};

wishListCardStyles.extendedInfoGridContainer = {
    gap: 0,
    css: css`
        min-height: 100%;

        ${screen("gt", "md")} {
            > ${GridOffset} {
                min-height: 91px;
            }
        }

        ${screen("only", "md")} {
            > ${GridOffset} {
                min-height: 58px;
            }
        }
    `,
};

wishListCardStyles.actionOverflowMenu = {
    cssOverrides: {
        wrapper: css`
            && {
                position: absolute;
                top: -8px;
                right: -8px;
            }
        `,
    },
};

wishListCardStyles.actionDeleteLink = {
    ...removeLinkStyle,
    css: css`
        ${removeLinkStyle?.css}
        line-height: 15px;
        justify-content: center;
        margin-top: 15px;
        width: 100%;
    `,
};

wishListCardStyles.wishListImage = {
    css: css`
        padding: 7.5px;
        ${screen("lte", "xs")} {
            padding: 5px;
        }
    `,
};

wishListCardStyles.wrapper = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        position: relative;
        flex: 1;
    `,
};

wishListCardStyles.wishListInfoWrapper = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        flex: 1;
    `,
};

wishListCardStyles.actionsWrapper = {
    css: css`
        display: flex;
        flex: 0;
        padding: 0;
        justify-content: center;

        ${screen("lte", "sm")} {
            flex: 1 1 100%;
            border-left-width: 0;
            justify-content: flex-start;
            padding: 0;
        }
    `,
};

wishListCardStyles.actionLeaveLink = {
    icon: {
        iconProps: {
            src: SignOutAlt,
            size: 13,
            css: css`
                margin-right: 5px;
            `,
        },
    },
    css: css`
        line-height: 15px;
        justify-content: center;
        margin-top: 15px;
        width: 100%;
    `,
};

wishListCardStyles.actionFavoriteClickable = {
    css: css`
        margin: 0 -10px;
    `,
};

wishListCardStyles.tagTooltip = {
    iconProps: {
        src: Tag,
        size: 15,
        css: css`
            margin: 4px -10px;
        `,
    },
};
